import { useEffect, useSyncExternalStore } from "react";
import { readCanonicalRoute, resolvedLegacyRoute, rememberCanonicalPayload, subscribeCanonicalPayloads } from "../../../../lib/copy/src/public-canonical-client.mjs";
import { isDriveshareLegacySeoEnabled } from "../../../../lib/copy/src/legacy-seo-switch.mjs";

const requests = new Map<string, Promise<void>>();
const failures = new Map<string, Error>();
const unchanged = /^\/(?:$|(?:us|uk|ie|ca|au|nz|za|de|fr|es|it|pt|dk|se|mt|br|co|ec|mx|be|pl|sk|no|bg|fi|nl|at|cz|in|ch|search|sign-up|signup|login|auth|account|dashboard|profile|messages|trips|bookings?|checkout|settings|forgot-password|reset-password|help|blog|guides|models|cities|compare)(?:\/|$))/i;
const listeners = new Set<() => void>();
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  const unsubscribe = subscribeCanonicalPayloads(listener);
  return () => { listeners.delete(listener); unsubscribe(); };
};
/** Initial SSR is preseeded; CSR waits for an explicit result, not a guessed route. */
export function usePublicCanonicalRoute(pathname: string) {
  // Response capabilities have no CMS/canonical lookup dependency. Preserve
  // their issued URL and never send a bearer token to the canonical resolver.
  const probeResponse = /^(?:\/[a-z]{2}(?:-[a-z]{2})?){0,2}\/probe-respond\/[^/]+\/?$/i.test(pathname);
  const needed = !probeResponse && isDriveshareLegacySeoEnabled() && !unchanged.test(pathname);
  const result = useSyncExternalStore(subscribe,
    () => readCanonicalRoute(pathname), () => readCanonicalRoute(pathname));
  const server = typeof window === "undefined" ? resolvedLegacyRoute(pathname, needed) : null;
  useEffect(() => {
    if (!needed || result || requests.has(pathname)) return;
    const promise = fetch(`${import.meta.env.BASE_URL || "/"}api/public-canonical/page?path=${encodeURIComponent(pathname)}`)
      .then(async response => {
        if (!response.ok) throw Error(`Canonical route lookup failed (${response.status})`);
        rememberCanonicalPayload(await response.json());
      }).catch(error => { failures.set(pathname, error); })
      .finally(() => { for (const listener of listeners) listener(); });
    requests.set(pathname, promise);
  }, [pathname, needed, result]);
  return { page: server || result?.on || null,
    pending: needed && !server && !result && !failures.has(pathname),
    error: failures.get(pathname) };
}
